import {
  PathsV1CompanyGroupSearchPostParametersQueryExtend,
  PathsV1CompanyGroupSearchPostParametersQuerySelect,
  PathsV1CompanyGroupUuidGetParametersQueryExtend,
} from "@/api-schema-enums";
import { useCompanyGroupByUuidQuery } from "@/common/api/company-group/use-company-group-by-uuid.query";
import { useCompanyGroupSearchQuery } from "@/common/api/company-group/use-company-group-search.query";
import { useCompanyGroupUpdateMutation } from "@/common/api/company-group/use-company-group-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { CompanyGroupIdentifierFormCard } from "@/modules/masterdata/company-group/_components/company-group-identifier-form-card";
import {
  companyGroupIdentifierFormDefaultValues,
  companyGroupIdentifierFormSchema,
  getDuplicateCompanyGroupIdentifierKeys,
  toCompanyGroupIdentifierDtos,
  toCompanyGroupIdentifierFormValues,
} from "@/modules/masterdata/company-group/company-group-identifiers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2 } from "lucide-react";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

export const CompanyGroupUpdateFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("masterdata:NameIsRequired")),
    identifiers: companyGroupIdentifierFormSchema,
  });

export type CompanyGroupUpdateFormSchemaType = z.infer<ReturnType<typeof CompanyGroupUpdateFormSchema>>;

interface CompanyGroupUpdateFormFieldsProps {
  uuid: string;
}

export function CompanyGroupUpdateFormFields({ uuid }: CompanyGroupUpdateFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();

  const { data: companyGroup, isLoading } = useCompanyGroupByUuidQuery(uuid, {
    parameters: { extend: [PathsV1CompanyGroupUuidGetParametersQueryExtend.CompanyGroupIdentifiers] },
  });
  const { mutateAsync, isPending } = useCompanyGroupUpdateMutation();

  const form = useForm<CompanyGroupUpdateFormSchemaType>({
    resolver: zodResolver(CompanyGroupUpdateFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { name: "", identifiers: companyGroupIdentifierFormDefaultValues },
    values: companyGroup
      ? {
          name: companyGroup.name || "",
          identifiers: toCompanyGroupIdentifierFormValues(companyGroup.companyGroupIdentifiers),
        }
      : undefined,
  });

  const watchedIdentifiers = useWatch({ control: form.control, name: "identifiers" });

  const { data: existingCompanyGroups, isPending: isExistingCompanyGroupsLoading } = useCompanyGroupSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1CompanyGroupSearchPostParametersQuerySelect.Uuid,
          PathsV1CompanyGroupSearchPostParametersQuerySelect.CompanyGroupIdentifiers,
        ],
        extend: [PathsV1CompanyGroupSearchPostParametersQueryExtend.CompanyGroupIdentifiers],
      },
    },
  });

  const onSubmit = async (values: CompanyGroupUpdateFormSchemaType) => {
    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            uuid,
            name: values.name,
            companyGroupIdentifiers: toCompanyGroupIdentifierDtos(values.identifiers),
          }),
        successMessage: t("masterdata:CompanyGroupSuccessfullyUpdated"),
        errorMessage: t("masterdata:ErrorUpdatingCompanyGroup"),
        customErrors: {
          409: t("masterdata:CompanyGroupIdentifierConflict"),
        },
      });
      navigate("~/masterdata/company-group");
    } catch (error) {
      console.error("Error updating company group:", error);
    }
  };

  const duplicateValidation = useMemo(() => {
    const duplicateKeys = getDuplicateCompanyGroupIdentifierKeys(
      existingCompanyGroups?.data,
      watchedIdentifiers ?? companyGroupIdentifierFormDefaultValues,
      uuid,
    );

    return {
      duplicateKeys,
      hasErrors: duplicateKeys.size > 0,
      errors: duplicateKeys.size > 0 ? [t("masterdata:CompanyGroupIdentifierConflict")] : [],
    };
  }, [existingCompanyGroups?.data, t, uuid, watchedIdentifiers]);

  const handleSubmitWithValidation = (values: CompanyGroupUpdateFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  const isSubmitDisabled =
    isPending || isExistingCompanyGroupsLoading || duplicateValidation.hasErrors || !form.formState.isValid;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <div className="space-y-6">
          <Card className="shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t("common:BasicInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Name")} *</FormLabel>
                    <FormControl>
                      <Input placeholder={t("masterdata:EnterCompanyGroupName")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <CompanyGroupIdentifierFormCard
            duplicateKeys={duplicateValidation.duplicateKeys}
            isCheckingDuplicates={isExistingCompanyGroupsLoading}
          />

          <SystemInfoFields
            uuid={companyGroup?.uuid}
            createdAt={companyGroup?.createdAt}
            updatedAt={companyGroup?.updatedAt}
          />

          <FormCardActions
            cancelLabel={t("common:Cancel")}
            onCancel={() => window.history.back()}
            isPending={isPending}
            submitDisabled={isSubmitDisabled}
            submitLabel={t("common:SaveChanges")}
          />
        </div>
      </form>
    </Form>
  );
}
